import { Link, usePage } from '@inertiajs/react';
import { Avatar, Box, Group, Menu, Text, useMatches } from '@mantine/core';
import {
  IconArrowBackUp,
  IconCalendarEvent,
  IconChartBar,
  IconInbox,
  IconLogout,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import NavButton from '@/components/layout/NavButton';
import NavbarLogo from '@/components/layout/NavbarLogo';
import SpotlightSearchButton from '@/components/layout/SpotlightSearchButton';
import OrganizationSettingsMenu from '@/components/organizations/OrganizationSettingsMenu';
import { getDisplayName } from '@/helpers/account';
import { useAccountOrganizations } from '@/hooks/useAccountOrganizations';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { SharedProps } from '@/types';

import classes from './TopNavbar.module.css';

export default function TopNavbar() {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const isDesktop = useMatches({ base: false, md: true });
  const accountOrganizations = useAccountOrganizations();
  const {
    current_account: currentAccount,
    current_organization: currentOrganization,
    organization_id: pageOrganizationId,
  } = usePage<SharedProps & { organization_id?: string }>().props;
  const selectedOrganizationId = pageOrganizationId ?? currentOrganization?.id;
  const selectedOrganizationQuery = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
  const currentAccountName = currentAccount
    ? [currentAccount.first_name, currentAccount.last_name].filter(Boolean).join(' ').trim()
    : '';
  const settingsOrganization =
    accountOrganizations.find((organization) => organization.id === selectedOrganizationId) ?? accountOrganizations[0];

  return (
    <Box
      component='header'
      className={classes.header}
      py='xs'
      px={isMobile ? 'var(--nav-padding, var(--mantine-spacing-sm))' : 'lg'}
      style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
    >
      <Group justify='space-between' align='center' gap='xs' wrap='nowrap'>
        <Group align='center' gap={isMobile ? 'var(--nav-gap, 4px)' : 'xs'} wrap='nowrap' style={{ minWidth: 0 }}>
          <NavbarLogo />
          {currentAccount?.is_pro && (
            <>
              <NavButton
                href={`/pro/conversations${selectedOrganizationQuery}`}
                activePrefix='/pro/conversations'
                label={t('menu.inbox')}
                icon={<IconInbox size={20} />}
              />
              <NavButton href='/pro/encounters' label={t('menu.activity')} icon={<IconCalendarEvent size={20} />} />
              <NavButton
                href={`/pro/patients${selectedOrganizationQuery}`}
                activePrefix='/pro/patients'
                label={t('menu.patients')}
                icon={<IconUsers size={20} />}
              />
              <NavButton
                href={`/pro/stats${selectedOrganizationQuery}`}
                activePrefix='/pro/stats'
                label={t('menu.stats')}
                icon={<IconChartBar size={20} />}
              />
            </>
          )}
          {settingsOrganization && (
            <OrganizationSettingsMenu
              organizationId={settingsOrganization.id}
              showSubscription={settingsOrganization.is_online_billing}
            />
          )}
        </Group>

        {!currentAccount && (
          <Group gap='xs' wrap='nowrap'>
            <NavButton
              href='/login'
              label={t('sessions.sign_in')}
              icon={isDesktop ? <IconUser size={18} aria-hidden='true' /> : undefined}
            />
          </Group>
        )}

        {currentAccount && (
          <Group align='center' gap={isMobile ? 'var(--nav-gap, var(--mantine-spacing-md))' : 'lg'} wrap='nowrap'>
            <SpotlightSearchButton />
            <Menu>
              <Menu.Target>
                <Avatar
                  name={currentAccountName || undefined}
                  color={currentAccountName ? 'initials' : 'blue'}
                  radius='xl'
                  style={{ cursor: 'pointer' }}
                />
              </Menu.Target>
              <Menu.Dropdown miw={240}>
                <Menu.Label>{t('accounts.signed_in_as')}</Menu.Label>
                <Menu.Item component={Link} href='/account/edit'>
                  <Box maw={208}>
                    <Text size='sm' fw={500} truncate>
                      {getDisplayName(currentAccount)}
                    </Text>
                    <Text size='xs' c='dimmed' truncate>
                      {currentAccount.email}
                    </Text>
                  </Box>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item component={Link} href='/account/edit' leftSection={<IconUser size={16} />}>
                  {t('accounts.my_account')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item component={Link} href='/patient/conversations' leftSection={<IconArrowBackUp size={16} />}>
                  {t('accounts.personal_space')}
                </Menu.Item>
                <Menu.Item component={Link} href='/session' method='delete' leftSection={<IconLogout size={16} />}>
                  {t('common.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        )}
      </Group>
    </Box>
  );
}
